import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { yachts } from "@/data/yachts";
import heroImage from "@/assets/ibiza-yachts-hero.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const Yachts = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Yachts Fleet" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Ibiza · Season 2026
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Yachts
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Private yacht charters across the Mediterranean's most sought-after destinations.
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Our Fleet</span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              Explore our curated collection of luxury yachts available for charter in Ibiza. 
              From intimate day cruisers to impressive superyachts, each vessel is maintained to the highest 
              standards and comes with experienced crew to ensure an unforgettable day at sea.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Fleet Grid */}
      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {yachts.map((yacht, i) => (
              <motion.div
                key={yacht.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 1, delay: (i % 3) * 0.1, ease }}
              >
                <Link to={`/yachts/${yacht.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <img
                      src={yacht.image}
                      alt={`${yacht.name} "${yacht.boatName}"`}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-display-medium text-base md:text-lg text-foreground tracking-wide uppercase mb-1">
                    {yacht.name}
                  </h3>
                  <p className="font-accent text-muted-foreground text-sm italic mb-2">
                    "{yacht.boatName}"
                  </p>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-label text-bronze text-xs">From {yacht.pricing.low}/day</span>
                    <span className="font-body text-muted-foreground text-xs">· {yacht.length}</span>
                    <span className="font-body text-muted-foreground text-xs">· {yacht.guests}</span>
                  </div>
                  <span className="font-label text-foreground/60 text-[10px] border-b border-foreground/20 pb-0.5 group-hover:text-foreground group-hover:border-foreground/50 transition-colors duration-700">
                    View details
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Yacht Requests
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm mt-4">
              A simple process, designed with ease in mind.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                step: "1",
                title: "Choose your yacht",
                text: "Browse our collection or simply send us a message. Whether you already have a yacht in mind or prefer guidance, our team will assist you in selecting the right vessel for your day at sea.",
              },
              {
                step: "2",
                title: "Share your preferences",
                text: "Let us know your desired date, destination and budget. We quietly check availability and arrange every detail with care.",
              },
              {
                step: "3",
                title: "Enjoy the experience",
                text: "Once confirmed, everything is prepared for your arrival. Step aboard, unwind, and allow the sea to set the rhythm of your day.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl md:text-6xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-lg text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
          >
            {(() => {
              const faqItems = [
                { q: "How does booking a yacht through Arzani work?", a: "Once you submit your request, our team will present suitable yacht options based on your preferences. After selecting your yacht, we secure the booking and arrange every detail for your day at sea." },
                { q: "How long can a yacht be reserved without payment?", a: "A yacht can be held for up to 24 hours. After this period, a deposit is required to confirm and secure the booking." },
                { q: "What is included in a yacht charter?", a: "Most charters include the yacht, professional crew, insurance, and standard onboard amenities. Fuel, food, and additional services may vary depending on the yacht and will be confirmed in advance." },
                { q: "How many guests can join a yacht charter?", a: "Most yachts accommodate up to 12 guests, in accordance with local regulations. Specific capacities vary per yacht." },
                { q: "Where do the yachts depart from?", a: "Most yachts depart from Marina Botafoch in Ibiza, though alternative departure locations can be arranged upon request." },
                { q: "Can you arrange routes to Formentera?", a: "Yes. Formentera is one of the most popular routes, known for its turquoise waters and beachside restaurants. Our team can curate your route based on your preferences." },
                { q: "What happens in case of bad weather?", a: "In case of unsuitable weather conditions, the charter may be rescheduled or adjusted depending on availability and safety conditions." },
                { q: "Is the deposit refundable?", a: "If a yacht booking is cancelled on the same day as the charter, the deposit is non-refundable. Other cancellation conditions will be communicated at the time of booking." },
                { q: "Can food, drinks, or special requests be arranged?", a: "Yes. We can arrange catering, beverages, DJs, decorations, and other bespoke requests to personalize your experience." },
                { q: "Do I need a license to rent a yacht?", a: "No. All yacht charters include a professional captain and crew." },
              ];
              const left = faqItems.slice(0, 5);
              const right = faqItems.slice(5);
              return (
                <>
                  <Accordion type="single" collapsible className="w-full">
                    {left.map((item, i) => (
                      <AccordionItem key={i} value={`faq-${i}`} className="border-foreground/10">
                        <AccordionTrigger className="font-display-medium text-sm md:text-base text-foreground tracking-wide uppercase text-left hover:no-underline py-5">{item.q}</AccordionTrigger>
                        <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.9]">{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <Accordion type="single" collapsible className="w-full">
                    {right.map((item, i) => (
                      <AccordionItem key={i} value={`faq-r-${i}`} className="border-foreground/10">
                        <AccordionTrigger className="font-display-medium text-sm md:text-base text-foreground tracking-wide uppercase text-left hover:no-underline py-5">{item.q}</AccordionTrigger>
                        <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.9]">{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              );
            })()}
          </motion.div>

          {/* Still have a question? */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <p className="font-display-medium text-lg md:text-xl text-foreground tracking-wide uppercase mb-4">
              Still have a question?
            </p>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-8">
              Our team is happy to help with anything you need.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact / Inquiry */}
      <section id="yacht-inquire" className="section-spacing bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase mb-6">
              Request a Yacht
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-10">
              Send us a message with your preferred yacht, dates, and any special requests.
              Our team will respond promptly with availability and a tailored proposal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-muted-foreground/60 text-xs mt-8">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Yachts;
